import type { UnmatchedPatientUpload } from "@elation/api-client/src/stitch-web/models";

import { DocumentEntity } from "@src/features/documents";

import { CareProtocolOpportunityListSkeleton } from "../CareProtocolOpportunityListSkeleton";
import { CareProtocolOpportunityUploadGroup } from "../CareProtocolOpportunityUploadGroup";
import { EmptyCareProtocolOpportunities } from "../EmptyCareProtocolOpportunities";
import { ErrorCareProtocolOpportunities } from "../ErrorCareProtocolOpportunities";
import { type ExtractedCareGapsUploadState } from "../ExtractedCareGapsTab";
import { NeedsReviewPatientUploadCard } from "../NeedsReviewPatientUploadCard";
import {
  UploadHistoryProcessingRow,
  UploadHistoryTable,
} from "../UploadHistoryTable";
import type { CareProtocolOpportunityDateGroup } from "../uploadHistoryGrouping";

export interface AllUploadHistoryListProps {
  extracted: ExtractedCareGapsUploadState;
  isVisible: boolean;
  needsReview: {
    documents: UnmatchedPatientUpload[];
    isError: boolean;
    isLoading: boolean;
  };
  searchTerm?: string;
}

type AllUploadHistoryItem =
  | {
      kind: "extracted";
      uploadedAt: string;
      group: CareProtocolOpportunityDateGroup;
    }
  | {
      kind: "needs-review";
      uploadedAt: string;
      item: UnmatchedPatientUpload;
    };

function allUploadSortValue(item: AllUploadHistoryItem): number {
  const value = new Date(item.uploadedAt).getTime();
  return Number.isNaN(value) ? 0 : value;
}

export function AllUploadHistoryList({
  extracted,
  isVisible,
  needsReview,
  searchTerm = "",
}: AllUploadHistoryListProps) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const needsReviewDocuments = needsReview.documents.filter((item) => {
    if (!normalizedSearchTerm) return true;
    return [
      item.document.fileName,
      item.document.mimeType,
      item.reason,
      DocumentEntity.needsReviewIdentifierSummaryLabel(item.identifierSummary),
      JSON.stringify(item.identifierSummary),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedSearchTerm);
  });
  const extractedUploadGroups = extracted.uploadGroups.filter((group) => {
    if (!normalizedSearchTerm) return true;
    return group.items.some((item) =>
      [
        item.description,
        item.patient.firstName,
        item.patient.lastName,
        item.patient.dob,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearchTerm),
    );
  });
  const items: AllUploadHistoryItem[] = [
    ...extractedUploadGroups.map((group) => ({
      kind: "extracted" as const,
      uploadedAt: group.uploadedAt,
      group,
    })),
    ...needsReviewDocuments.map((item) => ({
      kind: "needs-review" as const,
      uploadedAt: item.completedAt,
      item,
    })),
  ].sort((a, b) => allUploadSortValue(b) - allUploadSortValue(a));

  if (extracted.isLoading || needsReview.isLoading) {
    return <CareProtocolOpportunityListSkeleton />;
  }

  if (extracted.isError) {
    return <ErrorCareProtocolOpportunities />;
  }

  if (items.length === 0 && extracted.activeUploadCount === 0) {
    if (needsReview.isError) return <ErrorCareProtocolOpportunities />;
    return <EmptyCareProtocolOpportunities />;
  }

  return (
    <>
      <UploadHistoryTable label="Upload history">
        <UploadHistoryProcessingRow count={extracted.activeUploadCount} />
        {items.map((item, index) =>
          item.kind === "extracted" ? (
            <CareProtocolOpportunityUploadGroup
              key={`extracted-${item.group.date}`}
              date={item.group.date}
              defaultOpen={index === 0}
              isVisible={isVisible}
              items={item.group.items}
              searchTerm={searchTerm}
              shouldPollForDocuments={extracted.activeUploadCount > 0}
              uploadedAt={item.group.uploadedAt}
            />
          ) : (
            <NeedsReviewPatientUploadCard
              key={`needs-review-${item.item.document.id}`}
              item={item.item}
            />
          ),
        )}
      </UploadHistoryTable>
      {needsReview.isError ? (
        <div className="mt-3 rounded-[12px] border border-border bg-background px-4 py-3 text-[13px] text-muted-foreground">
          Unable to load documents that need review.
        </div>
      ) : null}
    </>
  );
}
